import React, { useMemo, useState } from "react";
import { useAuthStore } from "../stores/authStores";

export interface FamilyAccount {
  account_id?: number;
  person_id?: number;
  member_id?: string;
  principal_id?: string;
  record_type?: string;
  relation_with_principal?: string;
  name?: string;
  avatar_url?: string;
  is_primary?: boolean;
  is_current?: boolean;
}

interface TriggerProps {
  currentAccount: FamilyAccount;
  currentName: string;
  currentLabel: string;
  isOpen: boolean;
  isSwitching: boolean;
  toggle: () => void;
}

interface AccountSwitcherProps {
  renderTrigger: (props: TriggerProps) => React.ReactNode;
  className?: string;
  menuClassName?: string;
}

const getAccountLabel = (account: FamilyAccount) => {
  if (account.is_primary || account.record_type?.toUpperCase() === "P") {
    return "Akun utama";
  }

  const relation = account.relation_with_principal?.trim();
  return relation && relation.toLowerCase() !== "dependent"
    ? relation
    : "Keluarga";
};

const AccountSwitcher: React.FC<AccountSwitcherProps> = ({
  renderTrigger,
  className = "",
  menuClassName = "",
}) => {
  const user = useAuthStore((state) => state.user);
  const switchAccount = useAuthStore((state) => state.switchAccount);
  const [isOpen, setIsOpen] = useState(false);
  const [switchingMemberId, setSwitchingMemberId] = useState<string | null>(null);

  const accounts = useMemo<FamilyAccount[]>(() => {
    if (Array.isArray(user?.family_members) && user.family_members.length > 0) {
      return user.family_members;
    }

    const insurance = user?.person?.person_insurance ?? user?.person?.personInsurance;

    return [{
      account_id: user?.id,
      person_id: user?.person?.id,
      member_id: insurance?.card_number,
      record_type: "P",
      name: user?.person?.name || user?.name || "Pengguna",
      avatar_url: user?.person?.avatar_url,
      is_primary: true,
      is_current: true,
    }];
  }, [user]);

  const currentAccount = accounts.find((account) => account.is_current)
    ?? accounts.find((account) => account.account_id === user?.id)
    ?? accounts[0]
    ?? {};
  const currentName = currentAccount?.name || user?.person?.name || user?.name || "Pengguna";
  const currentLabel = getAccountLabel(currentAccount);
  const isSwitching = switchingMemberId !== null;

  const handleSelect = async (account: FamilyAccount) => {
    if (!account.member_id || account.member_id === currentAccount.member_id) {
      setIsOpen(false);
      return;
    }

    try {
      setSwitchingMemberId(account.member_id);
      await switchAccount(account.member_id);
      setIsOpen(false);

      // Memastikan seluruh query dan state lokal halaman memakai token akun baru.
      window.location.reload();
    } catch (error: any) {
      alert(error.response?.data?.message || error.message || "Gagal mengganti akun");
    } finally {
      setSwitchingMemberId(null);
    }
  };

  return (
    <div className={className}>
      {renderTrigger({
        currentAccount,
        currentName,
        currentLabel,
        isOpen,
        isSwitching,
        toggle: () => {
          if (!isSwitching) setIsOpen((open) => !open);
        },
      })}

      {isOpen && (
        <div
          role="listbox"
          aria-label="Pilih akun"
          className={menuClassName}
        >
          {accounts.map((account) => {
            const selected = account.is_current
              || account.member_id === currentAccount?.member_id;
            const switching = switchingMemberId === account.member_id;

            return (
              <button
                key={account.member_id || account.account_id}
                type="button"
                role="option"
                aria-selected={selected}
                disabled={isSwitching}
                onClick={() => handleSelect(account)}
                className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-sm disabled:cursor-wait disabled:opacity-70 ${
                  selected
                    ? "bg-teal-50 text-teal-700"
                    : "bg-white text-gray-700 hover:bg-gray-50"
                }`}
              >
                <span className="min-w-0 text-left">
                  <span className="block truncate font-semibold">
                    {account.name || "Pengguna"}
                  </span>
                  <span className="block text-xs font-normal">
                    {switching ? "Mengganti akun..." : getAccountLabel(account)}
                  </span>
                </span>
                {selected && (
                  <span aria-hidden="true" className="font-bold">&#10003;</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AccountSwitcher;
